import { Component, effect, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { IncomingCallService } from '../incoming-call.services';
import { RingcentralWebphone, CallState } from '../ringcentral-webphone.service';

@Component({
    selector: 'app-incoming-call',
    imports: [RouterModule],
    templateUrl: './incoming-calls.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './incoming-calls.component.scss'
})
export class IncomingCallComponent implements OnInit, OnDestroy {
  incomingCallService = inject(IncomingCallService);
  webphoneService = inject(RingcentralWebphone);

  showPopup = this.incomingCallService.showPopup;
  session: any;
  incomingNumber: string | null = null;
  displayName: string | null = null;
  leadId: string | null = null;
  caller_data: any; // remember lead metadata for widget/registration
  private sessionSubscription!: Subscription;

  constructor() {
    effect(() => {
      const isPopupVisible = this.showPopup();
      const state = this.webphoneService.inboundCallState();
      
      // Close popup when call ends, fails, goes idle, or gets connected (answered)
      if (isPopupVisible && (
        state === CallState.ENDED || 
        state === CallState.FAILED || 
        state === CallState.IDLE ||
        state === CallState.CONNECTED
      )) {
        this.incomingCallService.clearIncomingCall();
      }
    });
  }

  ngOnInit(): void {
    this.webphoneService.checkForSip();
    this.sessionSubscription = this.incomingCallService.incomingCall$.subscribe( async (session) => {
      this.session = session;
      if (session) {
        this.incomingNumber = this.getIncomingNumber(session);
        let leadsDetails: any = null;
        try {
          leadsDetails = await this.incomingCallService.fetchLeadDetails(this.incomingNumber);
        } catch (err) {
          // API lookup failed; we'll just fall back to raw number
          console.warn('Lead lookup failed', err);
        }

        this.caller_data = {
          caller: this.incomingNumber,
          lead_id: leadsDetails?.lead_id
        };
        // this.webphoneService.incomming_call_data = this.caller_data; // store in service for later use during call save if needed

        // keep the lead info locally for template navigation
        this.leadId = leadsDetails?.lead_id ?? null;
        this.displayName = leadsDetails?.lead_name || leadsDetails?.practice_name || leadsDetails?.contact_name || this.incomingNumber;

        // populate widget user details so name/number appear on ring
        // prefer values returned from lead lookup; fall back to the raw
        // incoming number or an explicit "Unknown Caller" label.
        let apiNumber: string | null = null;
        if (leadsDetails) {
          const cc = leadsDetails.practice_mobile_country_code || leadsDetails.alternate_mobile_country_code;
          const mob = leadsDetails.practice_mobile || leadsDetails.alternate_mobile || leadsDetails.phone || leadsDetails.mobile;
          if (mob) {
            apiNumber = cc ? `${cc}${mob}` : mob;
          }
        }
        const displayNumber = apiNumber || this.incomingNumber || 'Unknown Caller';

        const onCallUserDetails = {
          maskedPracticePhoneNumber: displayNumber,
          username: this.displayName
        };
        this.webphoneService.saveCall(session, '', this.caller_data); // save the call immediately with whatever info we have; we'll update it with more accurate details once the call is accepted
        this.webphoneService.updateSessionDetails(this.session, this.caller_data, onCallUserDetails);
      }
    });
  }


  ngOnDestroy(): void {
    this.sessionSubscription?.unsubscribe();
  }

  async answer() {
    if (this.session) {
      try {
        // Stop ringtone immediately when user clicks Accept
        this.webphoneService.stopRingtone(true);
        this.webphoneService.clearAutoTimeout();
        
        // Auto-hold existing calls
        const activeSessions = Object.values(this.webphoneService.activeSessions());
        for (const s of activeSessions) {
          if (s.id !== this.webphoneService.getSessionId(this.session) && !s.isOnHold) {
            console.log('Auto-holding existing call:', s.id);
            this.webphoneService.toggleHold(s.id);
          }
        }

        await this.session.answer();
        const sessionId = this.webphoneService.getSessionId(this.session);
        this.webphoneService.updateSessionState(sessionId, { state: CallState.CONNECTED });
        this.webphoneService.startTimer(sessionId);
        // hide the incoming call modal once the call is accepted
        this.incomingCallService.clearIncomingCall();
      } catch (e) {
        console.error('Failed to answer the call.', e);
      }
    }
  }

  async reject() {
    await this.decline();
  }

  async decline() {
    // Stop ringtone immediately
    this.webphoneService.stopRingtone(true);
    this.webphoneService.clearAutoTimeout();
    
    if (this.session) {
      const sessionId = this.webphoneService.getSessionId(this.session);
      console.log('--- decline triggered ---', { sessionId });
      try {
        await this.session.reject(); // RC Webphone usually uses reject() or decline()
        this.webphoneService.inboundCallState.set(CallState.ENDED);
        this.webphoneService.callState.set(CallState.ENDED);
        this.webphoneService.endCall(this.session);
      } catch (e) {
        console.error('Failed to reject the call.', e);
        // try terminate as fallback
        this.webphoneService.endCall(this.session);
      }
    }
    this.incomingCallService.clearIncomingCall();
  }

  close() {
    if (this.session && this.webphoneService.inboundCallState() === CallState.RINGING) {
      this.decline();
    } else if (this.session && this.webphoneService.inboundCallState() === CallState.CONNECTED) {
      this.webphoneService.endCall(this.session);
    }
    this.incomingCallService.clearIncomingCall();
  }

  getIncomingNumber(session: any): string | null {
    const headers = session?.sipMessage?.headers;
    if (!headers) return null;

    const prc = headers['P-rc'];
    if (prc) {
      const match = prc.match(/Phn="([^"]+)"/);
      if (match) {
        return match[1]; 
      }
    }

    const from = headers['From'];
    if (from) {
      const match = from.match(/sip:([^@>;]+)/);
      if (match) {
        return match[1];
      }
    }

    return null;
  }
}